import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { HopRound, FractionHopsState } from '../../../interfaces/fraction-hops.interface';
import { FractionHopsStageComponent } from './fraction-hops-stage.component';

/**
 * Host shell for Fraction Hops. Reads `FractionHopsState` from
 * `MiniGameService.session$`, drives the stage's HOP / UNDO / LAND via
 * @ViewChild, and writes outcomes with `scoreFractionHopsRound` +
 * `advanceFractionHopsRound`.
 *
 * LAND with the right number of jumps → success (product reveal), else
 * a red buzz and they adjust (Undo/Hop) and try again — no answer is
 * ever revealed on a miss (the multiplier is already in the prompt).
 */
@Component({
  selector: 'app-fraction-hops-game',
  templateUrl: './fraction-hops-game.component.html',
  styleUrls: ['./fraction-hops-game.component.scss']
})
export class FractionHopsGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(FractionHopsStageComponent) stage?: FractionHopsStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): FractionHopsState | null {
    return (this.session?.gameState as FractionHopsState) ?? null;
  }
  get round(): HopRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Controls ───────────────────────────────────────────────────────

  onHop(): void {
    if (this.resolving || !this.stage?.canHop) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.hop_();
  }

  onUndo(): void {
    if (this.resolving) return;
    this.stage?.undo();
  }

  onLand(): void {
    if (this.resolving || !this.stage?.canLand) return;
    this.stage.land();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreFractionHopsRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceFractionHopsRound(), 3000);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong count — buzz and let them fix it with Undo/Hop. The
    // multiplier is in the prompt, so nothing is being revealed.
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreFractionHopsRound(false);
    this.later(() => this.miniGameService.advanceFractionHopsRound(), 250);
  }
}
